import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { Checkbox, Popover } from '@mui/material';
import React from 'react';

import { Button } from '../Button.components';

export type CustomizableColumnKey =
  | 'date'
  | 'vendorValueName'
  | 'categoryValueName'
  | 'creatorDisplayName'
  | 'paymentMethodName'
  | 'reports'
  | 'total'
  | 'receipts'
  | 'transactionID'
  | 'desc'
  | 'approvalStatus';

export type ColumnVisibilityPrefs = Record<CustomizableColumnKey, boolean>;

export const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityPrefs = {
  date: true,
  vendorValueName: true,
  categoryValueName: true,
  creatorDisplayName: true,
  paymentMethodName: false,
  reports: true,
  total: true,
  receipts: true,
  transactionID: false,
  desc: false,
  approvalStatus: true,
};

interface ColumnVisibilityPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  visibility: ColumnVisibilityPrefs;
  onVisibilityChange: (visibility: ColumnVisibilityPrefs) => void;
  excludeKeys?: CustomizableColumnKey[];
}

export const ColumnVisibilityPopover: React.FC<ColumnVisibilityPopoverProps> = ({
  anchorEl,
  onClose,
  visibility,
  onVisibilityChange,
  excludeKeys = [],
}) => {
  const open = Boolean(anchorEl);

  const allColumns: { key: CustomizableColumnKey; label: string }[] = [
    { key: 'date', label: getTranslation('Date') },
    { key: 'vendorValueName', label: getTranslation('To/From') },
    { key: 'categoryValueName', label: getTranslation('Category') },
    { key: 'creatorDisplayName', label: getTranslation('Created By') },
    { key: 'paymentMethodName', label: getTranslation('Account') },
    { key: 'reports', label: getTranslation('Reports') },
    { key: 'total', label: getTranslation('Amount') },
    { key: 'receipts', label: getTranslation('Receipt') },
    { key: 'transactionID', label: getTranslation('Transaction #') },
    { key: 'desc', label: getTranslation('Notes') },
    { key: 'approvalStatus', label: getTranslation('Status') },
  ];

  const filteredColumns = allColumns.filter(({ key }) => !excludeKeys.includes(key));

  const visibleCount = Object.values(visibility).filter(Boolean).length;
  const allVisible = visibleCount === filteredColumns.length;

  function toggleColumn(key: CustomizableColumnKey) {
    if (visibility[key] && visibleCount <= 1) {
      return;
    }
    onVisibilityChange({ ...visibility, [key]: !visibility[key] });
  }

  function toggleSelectAll() {
    if (allVisible) {
      const deselected = Object.fromEntries(
        filteredColumns.map(({ key }, i) => [key, i === 0]),
      ) as ColumnVisibilityPrefs;
      onVisibilityChange(deselected);
    } else {
      const all = Object.fromEntries(
        filteredColumns.map(({ key }) => [key, true]),
      ) as ColumnVisibilityPrefs;
      onVisibilityChange(all);
    }
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      TransitionProps={{ timeout: 0 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          border: `1px solid ${theme.colors.grayXLight}`,
          width: 272,
          mt: 0.5,
        },
      }}
    >
      <Layout.Column py={4}>
        <Layout.Row px={16} py={10} align>
          <OpenSans.Secondary
            weight="bold-700"
            size="xs-12"
            style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
          >
            {getTranslation('Hide/Show Columns')}
          </OpenSans.Secondary>
        </Layout.Row>

        {filteredColumns.map(({ key, label }) => {
          const isChecked = visibility[key] ?? true;
          const isDisabled = isChecked && visibleCount <= 1;
          return (
            <Layout.PressableRow
              key={key}
              align
              bg="transparent"
              px={8}
              py={6}
              onClick={() => toggleColumn(key)}
              style={{ opacity: isDisabled ? 0.35 : 1 }}
            >
              <Checkbox
                checked={isChecked}
                disabled={isDisabled}
                size="small"
                sx={{
                  padding: '4px',
                  color: theme.colors.grayLight,
                  '&.Mui-checked': { color: theme.colors.brandPrimary },
                }}
                onChange={() => toggleColumn(key)}
                onClick={(e) => e.stopPropagation()}
              />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary size="s-16">{label}</OpenSans.Primary>
            </Layout.PressableRow>
          );
        })}

        <Layout.Row
          px={12}
          py={10}
          align
          justify="space-between"
          style={{ borderTop: `1px solid ${theme.colors.grayXLight}` }}
        >
          <button
            onClick={toggleSelectAll}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              borderRadius: 4,
              fontFamily: 'inherit',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: theme.colors.brandPrimary,
            }}
          >
            <Icon
              name={allVisible ? 'eye-off-outline' : 'eye-outline'}
              size={16}
              color={theme.colors.brandPrimary}
            />
            <OpenSans.Primary size="s-16" style={{ color: theme.colors.brandPrimary }}>
              {allVisible ? getTranslation('Hide all') : getTranslation('Show all')}
            </OpenSans.Primary>
          </button>
          <Button.Primary onClick={onClose} radius={50} py={8} px={20}>
            <OpenSans.Custom
              size={14}
              weight="bold-700"
              style={{ color: theme.colors.buttonWhite }}
            >
              {getTranslation('Done')}
            </OpenSans.Custom>
          </Button.Primary>
        </Layout.Row>
      </Layout.Column>
    </Popover>
  );
};
